<template>
  <div class="flex flex-col h-screen overflow-hidden">
    <div class="flex flex-1 min-h-0 p-6 relative">
      <!-- Notification -->
      <transition name="fade">
        <div 
          v-if="showNotification"
          class="fixed top-10 left-7 px-6 py-3 rounded-lg shadow-lg z-50 flex items-center"
          :class="{
            'bg-green-600 text-white': notificationType === 'success',
            'bg-red-600 text-white': notificationType === 'error',
            'bg-indigo-600 text-white': notificationType === 'info'
          }"
        >
          <i class="pi mr-2" :class="{
            'pi-check-circle': notificationType === 'success',
            'pi-times-circle': notificationType === 'error',
            'pi-info-circle': notificationType === 'info'
          }"></i>
          <span>{{ notificationMessage }}</span>
        </div>
      </transition>

      <!-- Mobile View Toggle (only visible on mobile) -->
      <div 
        class="fixed bottom-0 left-0 md:hidden z-50"
      >
        <button 
          @click="toggleMobileView" 
          class="bg-gradient-to-br from-slate-800 to-slate-700 text-white p-3 rounded-full shadow-lg focus:outline-none"
        >
          <i :class="showPdfInMobile ? 'pi pi-comments' : 'pi pi-file-pdf'"></i>
          <span class="sr-only">Toggle View</span>
        </button>
      </div>

      <!-- Main Resizable Layout (Desktop) -->
      <div class="flex w-full min-h-0 relative">
        <!-- Chat/Notepad Section -->
        <div 
          ref="chatPanel"
          :class="[
            'bg-gray-800 rounded-lg shadow-lg flex flex-col',
            'w-full min-h-0',
            {'hidden md:flex': showPdfInMobile}
          ]"
          :style="{ width: isMobile ? '100%' : `${chatPanelWidth}%` }"
        >
          <div class="flex items-center justify-between py-2 px-2 sm:px-4 bg-gradient-to-br from-slate-700 to-slate-800 text-white overflow-hidden">
            <h2 class="text-lg font-light truncate">{{ showChat ? 'Chat' : 'Notepad' }}</h2>
            <div class="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
              <!-- Add Document Button -->
              <button 
                v-if="showChat"
                @click="$refs.chatComponent.showDocumentUploader = true"
                class="flex items-center justify-center w-8 h-8 sm:w-auto sm:h-auto sm:px-2 sm:py-2 bg-gradient-to-r from-purple-500 to-purple-700 text-white hover:from-purple-600 hover:to-purple-800 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none"
              >
                <i class="pi pi-plus text-sm"></i>
                <span class="text-sm font-medium ml-1 hidden sm:inline">Add Doc</span>
              </button>
              
              <!-- Toolbar toggle button -->
              <div class="relative isolate" ref="toolbarDropdown">
                <button 
                  @click="toggleToolbar"
                  class="flex items-center justify-center w-8 h-8 sm:w-auto sm:px-2 sm:py-2 hover:bg-slate-600 rounded-lg transition-colors duration-200 focus:outline-none bg-slate-700"
                >
                  <i class="pi pi-bars text-sm"></i>
                  <span class="text-sm font-medium ml-1 hidden sm:inline">Tools</span>
                  <i class="pi pi-chevron-down text-xs ml-1 hidden sm:inline" :class="{ 'transform rotate-180': isToolbarOpen }"></i>
                </button>

                <!-- Toolbar dropdown -->
                <teleport to="body">
                  <transition
                    enter-active-class="transition ease-out duration-200"
                    enter-from-class="transform opacity-0 scale-95"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-150"
                    leave-from-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95"
                  >
                    <div
                      v-if="isToolbarOpen"
                      :style="{
                        position: 'fixed',
                        top: `${toolbarPosition.y}px`,
                        left: `${toolbarPosition.x}px`,
                        width: '12rem',
                        zIndex: 9999
                      }"
                      class="rounded-md shadow-xl bg-slate-700 ring-1 ring-black ring-opacity-5"
                    >
                      <div class="py-1">
                        <!-- Chat option -->
                        <button
                          @click="switchToChat"
                          class="w-full px-4 py-2 text-sm text-white hover:bg-slate-600 flex items-center"
                          :class="{ 'bg-slate-600': showChat }"
                        >
                          <i class="pi pi-comments mr-2"></i>
                          Chat View
                        </button>

                        <!-- Notepad option -->
                        <button
                          @click="switchToNotepad"
                          class="w-full px-4 py-2 text-sm text-white hover:bg-slate-600 flex items-center"
                          :class="{ 'bg-slate-600': !showChat }"
                        >
                          <i class="pi pi-file-edit mr-2"></i>
                          Notepad View
                        </button>
                      </div>
                    </div>
                  </transition>
                </teleport>
              </div>
            </div>
          </div>
          
          <div class="flex-1 overflow-y-auto min-h-0">
            <ChatUI 
              v-show="showChat" 
              ref="chatComponent" 
              :chat-session="chatSession"
              @add-to-notepad="handleAddToNotepad"
              @document-added="handleDocumentAdded"
            />
            <NotepadTab v-show="!showChat" ref="notepadComponent" />
          </div>
        </div>

        <!-- Resizable Divider (only visible on desktop) -->
        <div 
          v-show="!isMobile" 
          ref="divider"
          class="resizable-divider hidden md:flex"
          @mousedown="startResize"
          @touchstart="startTouch"
        >
          <div class="resize-handle">
            <div class="resize-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
        </div>

        <!-- PDF Section -->
        <div 
          ref="pdfPanel"
          :class="[
            'bg-gray-800 rounded-lg shadow-lg flex flex-col',
            'w-full min-h-0',
            {'hidden md:flex': !showPdfInMobile}
          ]"
          :style="{ width: isMobile ? '100%' : `${pdfPanelWidth}%` }"
        >
          <!-- Document Selector -->
          <div class="px-4 py-3 bg-gradient-to-br from-slate-700 to-slate-800 text-white flex items-center justify-between">
            <h2 class="text-lg font-light">Document Viewer</h2>
            <div v-if="documentIds.length > 1" class="flex items-center space-x-2">
              <span class="text-sm text-gray-400">{{ currentDocumentIndex + 1 }} of {{ documentIds.length }}</span>
              <div class="flex space-x-1">
                <button 
                  @click="previousDocument" 
                  class="p-2 rounded-lg hover:bg-slate-600 transition-colors duration-200"
                  :disabled="currentDocumentIndex === 0"
                  :class="{ 'opacity-50 cursor-not-allowed': currentDocumentIndex === 0 }"
                >
                  <i class="pi pi-chevron-left"></i>
                </button>
                <button 
                  @click="nextDocument" 
                  class="p-2 rounded-lg hover:bg-slate-600 transition-colors duration-200"
                  :disabled="currentDocumentIndex === documentIds.length - 1"
                  :class="{ 'opacity-50 cursor-not-allowed': currentDocumentIndex === documentIds.length - 1 }"
                >
                  <i class="pi pi-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
          
          <div class="flex-1 overflow-y-auto px-4 min-h-0">
            <div v-if="pdfError" class="text-center text-white bg-red-500 p-2 rounded-lg mt-4">
              <p>There was an error loading the PDF. Please try again later.</p>
            </div>
            <div v-else-if="pdfLoading" class="flex flex-col items-center justify-center h-full text-white p-8">
              <ProgressSpinner 
                class="mb-4"
                :style="{ width: '60px', height: '60px' }" 
                strokeWidth="4" 
                animationDuration=".7s"
              />
              <h3 class="text-xl font-medium mb-2">Loading PDF</h3>
              <p class="text-gray-400 text-center max-w-sm">
                {{ loadingMessage }}
              </p>
            </div>
            <div v-else class="pdf-container">
              <vue-pdf-app 
                :key="'pdf-viewer-' + pdfViewerKey" 
                class="pdf-viewer" 
                :pdf="pdfData" 
                v-if="pdfData" 
                @on-document-loaded="handlePDFUpload"
              ></vue-pdf-app>
              <div v-else class="text-center text-white p-8">
                <p>No PDF data available to display.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatUI from './ChatUI.vue';
import NotepadTab from './NotepadTab.vue';
import PdfReader from './PdfReader.vue';
// import PdfReaderNew from './PdfReaderNew.vue';
import axios from 'axios';
import { chatURL } from '@/socket';
import { mapMutations, mapGetters } from "vuex";
import { handleError, inject } from "vue";
import { useCookies } from "vue3-cookies";
import VuePdfApp from "vue3-pdf-app";
import ProgressSpinner from 'primevue/progressspinner';
import "vue3-pdf-app/dist/icons/main.css";

export default {
  name: 'PaperChat',
  components: { ChatUI, VuePdfApp, NotepadTab, ProgressSpinner },
  data() {
    return {
      pdfUrl: "",
      chatSession: "",
      pdfError: false,
      pdfData: "",
      pdfLoading: true,
      showChat: true,
      showNotification: false,
      notificationMessage: "",
      showPdfInMobile: false,
      isToolbarOpen: false,
      documentIds: [], // Array to store multiple document IDs
      currentDocumentIndex: 0, // Index of currently displayed document
      chatDetails: null, // Object to store chat details
      pdfViewerKey: 0, // Key to force re-render of PDF viewer
      documentCache: {}, // Cache to store previously loaded documents
      maxCacheSize: 10, // Maximum number of documents to keep in cache
      documentAccessTimes: {}, // Track when documents were last accessed
      notificationType: 'success', // Added for notification type
      notificationTimer: null, // Added for notification auto-hide
      // Resizing properties
      chatPanelWidth: 33, // Default chat panel width percentage
      pdfPanelWidth: 67, // Default PDF panel width percentage
      isDragging: false, // Track if user is currently resizing
      startX: 0, // Starting X position for resizing
      startChatWidth: 0, // Starting width of chat panel
      isMobile: false, // Check if we're in mobile view
      MIN_CHAT_WIDTH: 25, // Minimum chat panel width as percentage
      MAX_CHAT_WIDTH: 50, // Maximum chat panel width as percentage
      toolbarPosition: { x: 0, y: 0 },
      loadingMessage: "Loading document...", // Add this property for showing progress
    }
  },

  watch: {
    // Watch for changes in mobile PDF view toggle
    showPdfInMobile(newValue) {
      if (newValue === true) {
        // When switching to PDF view in mobile, refresh the viewer
        console.log('Mobile view switched to PDF, refreshing viewer');
        this.$nextTick(() => {
          this.refreshPdfViewer();
        });
      }
    }
  },

  methods: {
    switchToChat() {
      this.showChat = true;
      this.isToolbarOpen = false;
    },
    switchToNotepad() {
      this.showChat = false;
      this.isToolbarOpen = false;
    },
    sendMessage() {
      // Handle sending the message here
    },
    async handlePDFUpload(app) {
      console.log('PDF App:', app);
      const info = await app.pdfDocument
        .getMetadata()
        .catch((error) => {
          console.error('Error getting PDF metadata:', error);
          this.pdfError = true;
        });
    },
    
    // Start resize operation
    startResize(e) {
      this.isDragging = true;
      this.startX = e.clientX;
      this.startChatWidth = this.chatPanelWidth;
      
      // Add event listeners for mouse movement and release
      document.addEventListener('mousemove', this.resize);
      document.addEventListener('mouseup', this.stopResize);
      
      // Prevent text selection during resize
      document.body.style.userSelect = 'none';
      
      // Change cursor style and add dragging class
      document.body.style.cursor = 'col-resize';
      document.body.classList.add('dragging');
    },
    
    // For touch devices
    startTouch(e) {
      const touch = e.touches[0];
      this.isDragging = true;
      this.startX = touch.clientX;
      this.startChatWidth = this.chatPanelWidth;
      
      // Add touch event listeners
      document.addEventListener('touchmove', this.resizeTouch);
      document.addEventListener('touchend', this.stopResize);
      document.addEventListener('touchcancel', this.stopResize);
      
      // Add dragging class for styling
      document.body.classList.add('dragging');
      
      // Prevent scrolling during resize on touch devices
      e.preventDefault();
    },
    
    // Handle resize on mouse move
    resize(e) {
      if (!this.isDragging) return;
      
      // Calculate the change in width as a percentage of the container
      const containerWidth = this.$el.clientWidth;
      const dx = e.clientX - this.startX;
      const percentageDelta = (dx / containerWidth) * 100;
      
      // Calculate new widths
      let newChatWidth = this.startChatWidth + percentageDelta;
      
      // Apply constraints
      newChatWidth = Math.max(this.MIN_CHAT_WIDTH, Math.min(this.MAX_CHAT_WIDTH, newChatWidth));
      
      // Update panel widths
      this.chatPanelWidth = newChatWidth;
      this.pdfPanelWidth = 100 - newChatWidth;
      
      // Refresh PDF viewer to ensure proper layout
      this.refreshPdfViewerDebounced();
    },
    
    // Handle resize on touch move
    resizeTouch(e) {
      if (!this.isDragging) return;
      const touch = e.touches[0];
      
      // Calculate the change in width as a percentage of the container
      const containerWidth = this.$el.clientWidth;
      const dx = touch.clientX - this.startX;
      const percentageDelta = (dx / containerWidth) * 100;
      
      // Calculate new widths
      let newChatWidth = this.startChatWidth + percentageDelta;
      
      // Apply constraints
      newChatWidth = Math.max(this.MIN_CHAT_WIDTH, Math.min(this.MAX_CHAT_WIDTH, newChatWidth));
      
      // Update panel widths
      this.chatPanelWidth = newChatWidth;
      this.pdfPanelWidth = 100 - newChatWidth;
      
      // Refresh PDF viewer to ensure proper layout
      this.refreshPdfViewerDebounced();
    },
    
    // Stop resize operation
    stopResize() {
      this.isDragging = false;
      
      // Remove event listeners
      document.removeEventListener('mousemove', this.resize);
      document.removeEventListener('mouseup', this.stopResize);
      document.removeEventListener('touchmove', this.resizeTouch);
      document.removeEventListener('touchend', this.stopResize);
      document.removeEventListener('touchcancel', this.stopResize);
      
      // Restore default cursor and user-select
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.classList.remove('dragging');
      
      // Force refresh the PDF viewer with a slight delay
      setTimeout(() => {
        this.refreshPdfViewer();
      }, 200);
    },
    
    // Debounced version of refreshPdfViewer to avoid excessive updates
    refreshPdfViewerDebounced() {
      if (this._refreshTimeout) {
        clearTimeout(this._refreshTimeout);
      }
      this._refreshTimeout = setTimeout(() => {
        this.refreshPdfViewer();
      }, 100);
    },
    
    // Check if we're in mobile view
    checkMobileView() {
      this.isMobile = window.innerWidth < 768; // Match your md breakpoint
      
      // Reset to default widths when switching between mobile and desktop
      if (!this.isMobile) {
        // Only reset if it's a transition from mobile to desktop
        if (this.chatPanelWidth !== 33 && this.pdfPanelWidth !== 67) {
          this.chatPanelWidth = 33;
          this.pdfPanelWidth = 67;
        }
      }
    },

    async fetchChatDetails(loadFirstDocumentIfChanged = true) {
      try {
        const chatSession = this.$route.params.p;
        console.log('Fetching chat details for session:', chatSession);
        
        // Set the chat session to component data property
        this.chatSession = chatSession;
        
        // Use VUE_APP_BASE_URL as the environment variable
        const url = `${process.env.VUE_APP_BASE_URL}/get-chat-details?chat_session_id=${chatSession}`;
        console.log('Fetching from URL:', url);
        
        const response = await fetch(url, {
          headers: {
            'Token': `Bearer ${this.$cookies.get('pb_token')}`,
            'User': this.$cookies.get('pb_user')
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const responseData = await response.json();
        console.log('Chat details received:', responseData);
        
        if (!responseData.chat_details) {
          throw new Error('No chat details received from server');
        }
        
        this.chatDetails = responseData.chat_details;
        console.log('Chat details processed:', this.chatDetails);
        
        // Get document IDs from response
        let newDocumentIds = [];
        if (this.chatDetails.documentIds && this.chatDetails.documentIds.SS) {
          newDocumentIds = this.chatDetails.documentIds.SS;
          console.log('Multiple document IDs found:', newDocumentIds);
        } else if (this.chatDetails.documentId && this.chatDetails.documentId.S) {
          // Fallback for single document
          newDocumentIds = [this.chatDetails.documentId.S];
          console.log('Single document ID found:', newDocumentIds);
        } else {
          console.error('No document IDs found in chat details');
          throw new Error('No document IDs found in chat details');
        }
        
        // Check if document list has changed
        const documentListChanged = 
          newDocumentIds.length !== this.documentIds.length || 
          !newDocumentIds.every((id, index) => id === this.documentIds[index]);
        
        console.log('Document list changed:', documentListChanged);
        
        // Update document IDs
        this.documentIds = newDocumentIds;
        
        // Only load the first document if requested and needed
        if (loadFirstDocumentIfChanged && this.documentIds.length > 0) {
          if (documentListChanged || !this.pdfData) {
            // Only reload if the document list changed or we don't have data
            console.log('Loading first document because document list changed or no PDF data');
            await this.loadDocument(0);
          }
        }
        
        return {
          documentListChanged,
          documentIds: newDocumentIds
        };
      } catch (error) {
        console.error('Error fetching chat details:', error);
        this.pdfError = true;
        this.pdfLoading = false;
        throw error;
      }
    },

    async loadDocument(index, retryCount = 0) {
      try {
        // Reset error state
        this.pdfError = false;
        
        // Show loading state
        this.pdfLoading = true;
        this.loadingMessage = "Loading document...";
        
        // Check if we have a valid index
        if (index < 0 || index >= this.documentIds.length) {
          throw new Error(`Invalid document index: ${index}. Available documents: ${this.documentIds.length}`);
        }
        
        // Get the document ID
        const docId = this.documentIds[index];
        console.log(`Loading document with ID: ${docId}`);
        
        // Check if document is already in cache
        if (this.documentCache[docId]) {
          console.log('Document found in cache');
          this.documentAccessTimes[docId] = Date.now(); // Update access time
          this.pdfData = this.documentCache[docId];
          this.currentDocumentIndex = index;
          this.pdfLoading = false;
          
          // Prefetch adjacent documents after a short delay
          setTimeout(() => {
            this.prefetchAdjacentDocuments(index);
          }, 500);
          
          return;
        }
        
        // Encode the document ID for URL
        const encoded_doc_id = encodeURIComponent(docId);
        
        // First get metadata to see if the document is chunked
        this.loadingMessage = "Checking document structure...";
        const metadataResponse = await fetch(
          `${process.env.VUE_APP_BASE_URL}/get-document-metadata?id=${encoded_doc_id}`,
          {
            headers: {
              'Token': `Bearer ${this.$cookies.get('pb_token')}`,
              'User': this.$cookies.get('pb_user')
            }
          }
        );

        if (!metadataResponse.ok) {
          throw new Error(`Failed to get document metadata: ${metadataResponse.status}`);
        }

        const metadata = await metadataResponse.json();
        console.log('Document metadata:', metadata);
        
        let pdfData;
        
        // Handle chunked documents
        if (metadata.is_chunked) {
          console.log(`Document is chunked into ${metadata.chunk_count} chunks`);
          this.loadingMessage = `Loading document in chunks (0/${metadata.chunk_count})...`;
          
          // For chunked documents, we need to create a PDF data URL
          // First, download all chunks and combine them
          const chunks = [];
          
          // Download all chunks sequentially
          for (let i = 0; i < metadata.chunk_count; i++) {
            this.loadingMessage = `Loading document in chunks (${i+1}/${metadata.chunk_count})...`;
            
            const chunkResponse = await fetch(
              `${process.env.VUE_APP_BASE_URL}/download-chunk?id=${encoded_doc_id}&chunk_index=${i}`,
              {
                headers: {
                  'Token': `Bearer ${this.$cookies.get('pb_token')}`,
                  'User': this.$cookies.get('pb_user')
                }
              }
            );

            if (!chunkResponse.ok) {
              throw new Error(`Failed to get chunk ${i}: ${chunkResponse.status}`);
            }

            const chunkData = await chunkResponse.json();
            if (!chunkData.data) {
              throw new Error(`No data in chunk ${i}`);
            }
            
            // Store each chunk's data
            chunks.push(chunkData.data);
          }
          
          // Properly decode, merge and construct a single PDF blob
          const combinedBuffer = this.combineChunks(chunks);

          // Create a Blob and an object URL that the PDF viewer can load
          const pdfBlob = new Blob([combinedBuffer], { type: 'application/pdf' });
          pdfData = URL.createObjectURL(pdfBlob);
          console.log('Created PDF Blob URL from combined chunks');
        } else {
          // For non-chunked documents, just get the single chunk
          console.log('Document is not chunked, loading directly');
          this.loadingMessage = "Loading document...";
          
          const response = await fetch(
            `${process.env.VUE_APP_BASE_URL}/download-chunk?id=${encoded_doc_id}&chunk_index=0&original=true`,
            {
              headers: {
                'Token': `Bearer ${this.$cookies.get('pb_token')}`,
                'User': this.$cookies.get('pb_user')
              }
            }
          );

          if (!response.ok) {
            throw new Error(`Failed to get document: ${response.status}`);
          }

          const responseData = await response.json();
          if (!responseData.data) {
            throw new Error('No data in response');
          }
          
          // Try to use the data directly as a data URL
          console.log('Response data:', responseData.data.slice(0, 20));
          console.log('Response data length:', responseData.data.length);
          try {
            // Create a data URL that the PDF viewer can use directly
            pdfData = `data:application/pdf;base64,${responseData.data}`;
            console.log('Created PDF data URL from single chunk');
          } catch (error) {
            console.error('Error creating data URL, falling back to ArrayBuffer:', error);
            // Fall back to ArrayBuffer if data URL doesn't work
            pdfData = this.base64ToArrayBuffer(responseData.data);
          }
        }
        console.log('PDF data:', pdfData.slice(0, 60));
        
        // Update cache and access time
        this.documentAccessTimes[docId] = Date.now();
        this.documentCache[docId] = pdfData;
        
        // Set the PDF data for display
        this.pdfData = pdfData;
        this.currentDocumentIndex = index;
        
        // Prefetch adjacent documents
        setTimeout(() => {
          this.prefetchAdjacentDocuments(index);
        }, 500);
        
        console.log('Document loaded successfully');
        
      } catch (error) {
        console.error(`Error loading document at index ${index}:`, error);
        
        // Handle retries
        if (retryCount < 2) {
          console.log(`Retrying (${retryCount + 1}/2)...`);
          this.loadingMessage = `Error loading document. Retrying (${retryCount + 1}/2)...`;
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait before retry
          return this.loadDocument(index, retryCount + 1);
        }
        
        this.pdfError = true;
        this.showNotificationMessage("Failed to load document", "error");
      } finally {
        this.pdfLoading = false;
      }
    },

    // Helper method to combine base64 chunks
    combineChunks(chunks) {
      /*
        Each element in `chunks` is a base-64 string that represents *only* the
        bytes of that individual chunk.  Concatenating the strings directly
        will usually produce an invalid base-64 payload because every chunk (bar
        maybe the last) contains its own padding characters ("=" / "==").  To
        reliably rebuild the original file we must:
        1. Base-64-decode every chunk back to its raw bytes.
        2. Concatenate those byte arrays in the original order.
        3. Return the combined ArrayBuffer so it can be passed to the PDF
           viewer or re-encoded as a single, valid base-64 string if needed.
      */
      try {
        // Decode each chunk to a Uint8Array, tracking total length
        const decodedChunks = [];
        let totalLength = 0;
        for (const base64 of chunks) {
          if (!base64) continue;
          // Remove line breaks that may be present in S3-encoded strings
          const clean = base64.replace(/[\r\n]+/g, "");
          const binaryString = atob(clean);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          decodedChunks.push(bytes);
          totalLength += len;
        }

        // Allocate combined buffer
        const combined = new Uint8Array(totalLength);
        let offset = 0;
        for (const part of decodedChunks) {
          combined.set(part, offset);
          offset += part.length;
        }
        return combined.buffer; // ArrayBuffer
      } catch (e) {
        console.error("Error combining chunks:", e);
        throw e;
      }
    },

    // Navigation methods for documents
    nextDocument() {
      if (this.currentDocumentIndex < this.documentIds.length - 1) {
        this.loadDocument(this.currentDocumentIndex + 1);
      }
    },

    previousDocument() {
      if (this.currentDocumentIndex > 0) {
        this.loadDocument(this.currentDocumentIndex - 1);
      }
    },

    base64ToArrayBuffer(base64) {
      try {
        // Remove potential problematic characters that might be in the response
        // but aren't valid for base64 decoding
        const cleanBase64 = base64.replace(/[\n\r]/g, '');
        
        // Create binary string from base64
        const binaryString = window.atob(cleanBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        
        // Convert binary string to array buffer
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        return bytes.buffer;
      } catch (error) {
        console.error('Error converting base64 to ArrayBuffer:', error);
        
        // If there's an error, try an alternative approach by using a data URL
        try {
          console.log('Trying alternative PDF loading approach...');
          // Create a Blob from the base64 data
          const byteCharacters = atob(base64.replace(/^data:application\/pdf;base64,/, '').replace(/[\n\r]/g, ''));
          const byteNumbers = new Array(byteCharacters.length);
          
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          
          const byteArray = new Uint8Array(byteNumbers);
          return byteArray.buffer;
        } catch (fallbackError) {
          console.error('Alternative approach also failed:', fallbackError);
          throw new Error('Failed to decode PDF data');
        }
      }
    },
    handleAddToNotepad(text) {
      this.$refs.notepadComponent.addNote(text);
      this.showNotificationMessage("Note added successfully!", "success");
    },
    showNotificationMessage(message, type = 'success', duration = 3000) {
      this.notificationMessage = message;
      this.notificationType = type; // Can be 'success', 'error', or 'info'
      this.showNotification = true;
      
      // Auto-hide the notification after the specified duration
      if (this.notificationTimer) {
        clearTimeout(this.notificationTimer);
      }
      
      this.notificationTimer = setTimeout(() => {
        this.showNotification = false;
      }, duration);
    },
    toggleMobileView() {
      this.showPdfInMobile = !this.showPdfInMobile;
    },
    // Force refresh the PDF viewer
    refreshPdfViewer() {
      // A short delay to ensure DOM is updated
      setTimeout(() => {
        // Increment key to force re-render
        this.pdfViewerKey++;
        console.log('PDF viewer refreshed with new key:', this.pdfViewerKey);
        
        // Try to trigger layout recalculation
        if (document.querySelector('.pdf-viewer')) {
          const viewer = document.querySelector('.pdf-viewer');
          const isMobile = window.innerWidth < 768;
          
          // Mobile-specific handling
          if (isMobile && this.showPdfInMobile) {
            console.log('Applying mobile-specific PDF refresh');
            viewer.style.height = 'calc(100vh - 120px)';
            viewer.style.width = '100%';
            viewer.style.display = 'block';
            
            // Find the PDF container inside vue-pdf-app and force refresh
            setTimeout(() => {
              const pdfContainer = document.querySelector('.vue-pdf-app iframe');
              if (pdfContainer) {
                pdfContainer.style.height = 'calc(100vh - 140px)';
                pdfContainer.style.width = '100%';
              }
            }, 200);
          } else {
            // Desktop handling
            const currentHeight = viewer.style.height;
            viewer.style.height = '100.1%';
            // Force layout recalculation
            viewer.offsetHeight;
            viewer.style.height = currentHeight || '100%';
          }
        }
      }, 300);
    },
    async handleDocumentAdded(documentId) {
      console.log('PaperChat: Document added with ID:', documentId);
      
      try {
        // Step 1: Show loading notification
        this.notificationMessage = "Loading your document...";
        this.notificationType = "info";
        this.showNotification = true;
        
        // Step 2: Set loading state
        this.pdfLoading = true;
        
        // Step 3: Clear document from cache if it exists
        if (this.documentCache[documentId]) {
          console.log('Clearing cached document');
          delete this.documentCache[documentId];
        }
        
        // Step 4: Refresh the document list but don't auto-load any document
        console.log('Refreshing document list...');
        const { documentIds } = await this.fetchChatDetails(false);
        console.log('Document IDs after refresh:', documentIds);
        
        if (!documentIds || documentIds.length === 0) {
          throw new Error('No documents available after refresh');
        }
        
        // Step 5: Find the new document in the list
        // Note: The document ID might have been normalized by the server
        const normalizedId = documentId.replaceAll(':', '_').replaceAll('/', '_');
        console.log('Looking for document with normalized ID:', normalizedId);
        
        // Try to find the document by ID or normalized ID
        let newDocIndex = documentIds.findIndex(id => 
          id === documentId || id === normalizedId);
        
        // If not found, use the last document as fallback
        if (newDocIndex < 0) {
          console.log('Document not found by ID, using last document');
          newDocIndex = documentIds.length - 1;
        }
        
        console.log('Selected document index:', newDocIndex);
        
        // Step 6: Load the document
        if (newDocIndex >= 0) {
          console.log('Loading document at index:', newDocIndex);
          await this.loadDocument(newDocIndex);
          
          // Step 7: Update notification
          this.notificationMessage = "Document loaded successfully!";
          this.notificationType = "success";
          this.showNotification = true;
          
          // Step 8: On mobile, switch to PDF view
          if (window.innerWidth < 768) {
            console.log('Mobile detected, switching to PDF view');
            this.showPdfInMobile = true;
            
            // Force a refresh of the PDF viewer since we're switching to it
            this.$nextTick(() => {
              this.refreshPdfViewer();
            });
          }
        } else {
          throw new Error('No documents available to load');
        }
      } catch (error) {
        console.error('Error handling new document:', error);
        this.notificationMessage = "Error loading document. Please try refreshing the page.";
        this.notificationType = "error";
        this.showNotification = true;
        this.pdfError = true;
      } finally {
        // Ensure loading state is reset
        this.pdfLoading = false;
        
        // Auto-hide notification after 3 seconds
        setTimeout(() => {
          this.showNotification = false;
        }, 3000);
      }
    },
    // Prefetch adjacent documents to improve navigation experience
    prefetchAdjacentDocuments(currentIndex) {
      // Don't prefetch if we only have one document
      if (this.documentIds.length <= 1) return;
      
      // Schedule with setTimeout to avoid blocking the main thread
      setTimeout(() => {
        // Try to prefetch next document
        if (currentIndex < this.documentIds.length - 1) {
          const nextDocId = this.documentIds[currentIndex + 1];
          this.prefetchDocument(nextDocId);
        }
        
        // Try to prefetch previous document
        if (currentIndex > 0) {
          const prevDocId = this.documentIds[currentIndex - 1];
          this.prefetchDocument(prevDocId);
        }
      }, 1000); // Wait 1 second after current document is loaded
    },
    
    // Manage the size of the document cache
    manageCacheSize() {
      const cacheSize = Object.keys(this.documentCache).length;
      
      // If cache size is below max, no need to evict anything
      if (cacheSize < this.maxCacheSize) {
        return;
      }
      
      console.log(`Cache size (${cacheSize}) exceeds maximum (${this.maxCacheSize}). Removing least recently used documents.`);
      
      // Create a sorted list of documents by access time (oldest first)
      const sortedDocs = Object.keys(this.documentAccessTimes)
        .filter(id => this.documentCache[id]) // Only consider documents that are in the cache
        .sort((a, b) => this.documentAccessTimes[a] - this.documentAccessTimes[b]);
      
      // Calculate how many documents to remove
      const documentsToRemove = Math.max(1, Math.ceil(this.maxCacheSize * 0.2)); // Remove at least 1, or 20% of max
      
      // Remove the oldest documents
      for (let i = 0; i < documentsToRemove && i < sortedDocs.length; i++) {
        const docId = sortedDocs[i];
        console.log(`Removing document ${docId} from cache (least recently used)`);
        delete this.documentCache[docId];
      }
    },

    // Prefetch a single document in the background
    async prefetchDocument(docId) {
      // Skip if already in cache
      if (this.documentCache[docId]) {
        return;
      }
      
      try {
        console.log(`Prefetching document ID: ${docId}`);
        const encoded_doc_id = encodeURIComponent(docId);
        
        // First get metadata to see if it's chunked
        const metadataResponse = await fetch(`${process.env.VUE_APP_BASE_URL}/get-document-metadata?id=${encoded_doc_id}`, {
          headers: {
            'Token': `Bearer ${this.$cookies.get('pb_token')}`,
            'User': this.$cookies.get('pb_user')
          }
        });

        if (!metadataResponse.ok) {
          console.warn(`Prefetch metadata failed for document ${docId}`);
          return;
        }

        const metadata = await metadataResponse.json();
        console.log('Metadata:', metadata);
        // For prefetching, we're not showing progress, so we'll simplify the code
        let pdfData;
        
        if (metadata.is_chunked) {
          // For chunked documents, download only the first chunk for prefetch
          // This will at least allow the document to start rendering when selected
          const firstChunkResponse = await fetch(
            `${process.env.VUE_APP_BASE_URL}/download-chunk?id=${encoded_doc_id}&chunk_index=0`,
            {
              headers: {
                'Token': `Bearer ${this.$cookies.get('pb_token')}`,
                'User': this.$cookies.get('pb_user')
              }
            }
          );
          
          if (!firstChunkResponse.ok) {
            console.warn(`Prefetch chunk failed for document ${docId}`);
            return;
          }
          
          const chunkData = await firstChunkResponse.json();
          
          // Create a data URL for the PDF viewer
          pdfData = `data:application/pdf;base64,${chunkData.data}`;
        } else {
          // For non-chunked documents, use the original download approach
          const response = await fetch(
            `${process.env.VUE_APP_BASE_URL}/download-chunk?id=${encoded_doc_id}&chunk_index=0&original=true`,
            {
              headers: {
                'Token': `Bearer ${this.$cookies.get('pb_token')}`,
                'User': this.$cookies.get('pb_user')
              }
            }
          );

          if (!response.ok) {
            console.warn(`Prefetch failed for document ${docId}`);
            return;
          }

          const responseData = await response.json();
          console.log('Response data:', responseData.data.slice(0, 20));
          console.log('Response data length:', responseData.data.length);
          // Create a data URL for the PDF viewer
          pdfData = `data:application/pdf;base64,${responseData.data}`;
        }
        
        if (!pdfData) {
          console.warn(`No data in prefetch response for document ${docId}`);
          return;
        }
        
        // Manage cache size before adding new document
        this.manageCacheSize();
        
        // Update access time and store in cache
        this.documentAccessTimes[docId] = Date.now();
        this.documentCache[docId] = pdfData;
        console.log(`Document ${docId} prefetched and cached successfully`);
      } catch (error) {
        console.warn(`Error prefetching document ${docId}:`, error);
      }
    },

    // Handle window resize with debouncing
    handleWindowResize() {
      if (this._resizeTimeout) {
        clearTimeout(this._resizeTimeout);
      }
      
      this._resizeTimeout = setTimeout(() => {
        this.checkMobileView();
        this.refreshPdfViewer();
      }, 200);
    },

    toggleToolbar(event) {
      this.isToolbarOpen = !this.isToolbarOpen;
      
      // Calculate position for the dropdown menu
      if (this.isToolbarOpen && this.$refs.toolbarDropdown) {
        const rect = this.$refs.toolbarDropdown.getBoundingClientRect();
        this.toolbarPosition = {
          x: rect.right - 192, // 12rem (width of dropdown)
          y: rect.bottom + 8
        };
      }
      
      // Add event listener to close the dropdown when clicking outside
      if (this.isToolbarOpen) {
        this.$nextTick(() => {
          const handleClickOutside = (event) => {
            if (this.$refs.toolbarDropdown && !this.$refs.toolbarDropdown.contains(event.target)) {
              this.isToolbarOpen = false;
              document.removeEventListener('click', handleClickOutside);
            }
          };
          
          // Add the event listener with a slight delay to prevent immediate closure
          setTimeout(() => {
            document.addEventListener('click', handleClickOutside);
          }, 0);
        });
      }
    },
  },

  async mounted() {
    try {
      // Get chat session ID from route params
      const chatSession = this.$route.params.p;
      console.log('PaperChat: Component mounted, chat session:', chatSession);

      if (!chatSession) {
        console.error('PaperChat: No chat session ID provided');
        this.$router.push('/');
        return;
      }

      // Set the chat session to component data property
      this.chatSession = chatSession;
      
      // Check required environment variables
      if (!process.env.VUE_APP_BASE_URL) {
        console.error('PaperChat: Missing VUE_APP_BASE_URL environment variable');
        this.pdfError = true;
        return;
      }

      if (!process.env.VUE_APP_DOC_BASE_URL) {
        console.error('PaperChat: Missing VUE_APP_DOC_BASE_URL environment variable');
        this.pdfError = true;
        return;
      }

      console.log('PaperChat: All environment variables are available');

      // Initialize mobile/desktop view check
      this.checkMobileView();
      
      // Add window resize listener to check mobile/desktop view
      window.addEventListener('resize', this.handleWindowResize);

      // Wait for refs to be available
      this.$nextTick(async () => {
        // Check if the chat component is available
        if (this.$refs.chatComponent) {
          console.log('PaperChat: Chat component found, initializing...');
          // Initialize chat component
          this.$refs.chatComponent.startChat(chatSession);
        } else {
          console.error('PaperChat: Chat component not found!');
        }
        
        // Fetch chat details and load documents
        console.log('PaperChat: Fetching initial chat details');
        await this.fetchChatDetails(true);
        console.log('PaperChat: Initial setup complete');
      });
    } catch (error) {
      console.error('PaperChat: Error during component initialization:', error);
      this.pdfError = true;
      this.pdfLoading = false;
    }
  },

  beforeUnmount() {
    // Clear document cache to free memory
    this.documentCache = {};
    
    // Remove resize event listener
    window.removeEventListener('resize', this.handleWindowResize);
    
    // Close socket if open
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.close();
    }
  }
};

</script>

<style scoped>
/* Make the buttons translucent */
button {
  opacity: 0.8;
}

/* Make the buttons more opaque on hover */
button:hover {
  opacity: 1;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

i {
  color: white;
}

.tooltip {
  display: none;
}

.tooltip-group:hover .tooltip {
  display: block;
}

/* Resizable divider styles */
.resizable-divider {
  width: 8px;
  cursor: col-resize;
  background: transparent;
  position: relative;
  margin: 0 -4px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.resize-handle {
  width: 4px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.resizable-divider:hover .resize-handle,
.resizable-divider:active .resize-handle {
  background-color: rgba(255, 255, 255, 0.3);
}

.resize-dots {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
}

.dot {
  width: 3px;
  height: 3px;
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
}

.resizable-divider:hover .dot {
  background-color: rgba(255, 255, 255, 0.8);
}

/* Add active state for when dragging */
body.dragging .resizable-divider .resize-handle {
  background-color: rgba(255, 255, 255, 0.4);
}

body.dragging .dot {
  background-color: rgba(255, 255, 255, 0.9);
}

/* PDF container styles */
.pdf-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

:deep(.pdf-viewer) {
  height: 100%;
  width: 100%;
  overflow: hidden;
  flex: 1;
}

:deep(.vue-pdf-app) {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

:deep(.vue-pdf-app iframe) {
  height: 100% !important;
  width: 100%;
  border: none;
  overflow: hidden;
  flex: 1;
}

:deep(.vue-pdf-embed) {
  height: 100%;
  overflow: hidden;
}

/* Add responsive styles */
@media (max-width: 768px) {
  .p-6 {
    padding: 0.5rem;
  }
  
  .pdf-container {
    height: 100%;
  }
  
  :deep(.vue-pdf-app) {
    height: 100%;
  }
  
  :deep(.vue-pdf-app iframe) {
    height: 100% !important;
  }
}

/* Add these new styles */
.toolbar-enter-active,
.toolbar-leave-active {
  transition: all 0.2s ease;
}

.toolbar-enter-from,
.toolbar-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Click outside handling */
.toolbar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 40;
}

:deep(.p-progress-spinner-circle) {
  stroke: #6366f1 !important;
  stroke-width: 4px;
}

</style>
